import { useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import NumberField from './NumberField';
import ResultPanel from './ResultPanel';
import ToolLayout from './ToolLayout';
import { formatNumber, parseNumber } from '../utils/numbers';

function BeltSize() {
  const [beltType, setBeltType] = useState('v_belt');
  const [centerDistance, setCenterDistance] = useState('');
  const [largePulley, setLargePulley] = useState('');
  const [smallPulley, setSmallPulley] = useState('');

  const { result, error } = useMemo(() => {
    const c = parseNumber(centerDistance);
    const d1 = parseNumber(largePulley);
    const d2 = parseNumber(smallPulley);

    if (c == null || d1 == null || d2 == null) {
      return { result: null, error: '' };
    }
    if (c <= 0 || d1 <= 0 || d2 <= 0) {
      return { result: null, error: 'All dimensions must be greater than zero' };
    }
    if (d1 < d2) {
      return { result: null, error: 'Large pulley must be at least as big as the small pulley' };
    }

    const beltLength = 2 * c + (Math.PI * (d1 + d2)) / 2 + (d1 - d2) ** 2 / (4 * c);
    const speedRatio = d1 / d2;
    const arg = (d1 - d2) / (2 * c);
    const wrapAngle = Math.abs(arg) <= 1 ? 180 - 2 * Math.asin(arg) * (180 / Math.PI) : null;

    return { result: { beltLength, speedRatio, wrapAngle }, error: '' };
  }, [centerDistance, largePulley, smallPulley]);

  return (
    <ToolLayout title="Belt Size">
      <Card>
        <CardContent>
          <Typography color="text.secondary" paragraph>
            Calculate open-belt length, speed ratio, and wrap angle from pulley diameters and centre distance.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl>
                <InputLabel>Belt type</InputLabel>
                <Select value={beltType} label="Belt type" onChange={(event) => setBeltType(event.target.value)}>
                  <MenuItem value="v_belt">V-belt</MenuItem>
                  <MenuItem value="flat_belt">Flat belt</MenuItem>
                  <MenuItem value="timing_belt">Timing belt</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberField label="Centre distance (mm)" value={centerDistance} onChange={setCenterDistance} min={0} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberField label="Large pulley diameter (mm)" value={largePulley} onChange={setLargePulley} min={0} />
            </Grid>
            <Grid item xs={12}>
              <NumberField label="Small pulley diameter (mm)" value={smallPulley} onChange={setSmallPulley} min={0} />
            </Grid>
          </Grid>
          {error && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}
          {result && (
            <ResultPanel
              items={[
                { label: 'Belt length', value: `${formatNumber(result.beltLength, 1)} mm` },
                { label: 'Speed ratio', value: formatNumber(result.speedRatio, 2) },
                {
                  label: 'Wrap angle',
                  value: result.wrapAngle == null ? '—' : `${formatNumber(result.wrapAngle, 1)}°`,
                },
              ]}
            />
          )}
        </CardContent>
      </Card>
      <Divider sx={{ my: 2 }} />
      <Typography variant="body2" color="text.secondary">
        L = 2C + π(D1 + D2)/2 + (D1 − D2)² / (4C). Belt type is for reference; pitch diameter still needs to match the belt section you use.
      </Typography>
    </ToolLayout>
  );
}

export default BeltSize;
